/* eslint-disable */
import type { ReleaseResponse, ReleaseGenerateRequest } from '../types/release';

const API_BASE_URL = '/api/proxy/release-assistant-backend/api';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new ApiError(
      errorText || `HTTP error! status: ${response.status}`,
      response.status,
      response.statusText
    );
  }

  return response.json();
}

export async function generateRelease(
  request: ReleaseGenerateRequest,
  forceRefresh: boolean = false
): Promise<ReleaseResponse> {
  const url = `/release/generate?force_refresh=${forceRefresh}`;
  return fetchApi<ReleaseResponse>(url, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function getRelease(
  release: string
): Promise<ReleaseResponse> {
  return fetchApi<ReleaseResponse>(`/release/${release}`);
}

export async function refreshRelease(
  release: string
): Promise<ReleaseResponse> {
  return fetchApi<ReleaseResponse>(`/release/${release}/refresh`);
}

export async function healthCheck(): Promise<{ status: string; service: string; redis?: string }> {
  return fetchApi<{ status: string; service: string; redis?: string }>('/health');
}

// Report API functions
export interface ReportTemplateRequest {
  release: string;
}

export interface ReportTemplateResponse {
  markdown_template: string;
  release: string;
}

export interface SlackSendRequest {
  markdown_content: string;
  channel?: string;
  user?: string;
  release: string;
}

export interface SlackSendResponse {
  success: boolean;
  message: string;
  channel?: string;
  user?: string;
  timestamp?: string;
}

export interface SlackPreviewRequest {
  markdown_content: string;
}

export interface SlackPreviewResponse {
  mrkdwn_content: string;
  estimated_length: number;
  will_split: boolean;
}

export async function getReportTemplate(
  request: ReportTemplateRequest
): Promise<ReportTemplateResponse> {
  return fetchApi<ReportTemplateResponse>('/reports/template', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function sendSlack(
  request: SlackSendRequest
): Promise<SlackSendResponse> {
  return fetchApi<SlackSendResponse>('/reports/send-slack', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function previewSlack(
  request: SlackPreviewRequest
): Promise<SlackPreviewResponse> {
  return fetchApi<SlackPreviewResponse>('/reports/preview-slack', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}
